import moment, { Moment } from 'moment';

/**
 * England & Wales working-day helpers (NX-5537).
 *
 * Mirror of the backend `src/utils/workingDays.ts` (the source of truth, unit
 * tested there). Used to enforce the Bacs minimum-lead-time on the payment-date
 * picker: Bacs only processes on working days (Mon-Fri excluding England & Wales
 * bank holidays). Kept as a small standalone helper - no dependency / network.
 * One-off holidays that cannot be derived must be added to ONE_OFF_HOLIDAYS.
 */

const ONE_OFF_HOLIDAYS: readonly string[] = [];

const iso = (m: Moment): string => m.format('YYYY-MM-DD');
const isWeekend = (m: Moment): boolean => m.day() === 0 || m.day() === 6;

// Anonymous Gregorian algorithm (Computus) -> Easter Sunday for a Gregorian year.
const easterSunday = (year: number): Moment => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const n = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * n + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * n + 114) % 31) + 1;
  return moment({ year, month: month - 1, day });
};

const firstMonday = (year: number, month: number): Moment => {
  const d = moment({ year, month, day: 1 });
  return d.add((8 - d.day()) % 7, 'day');
};
const lastMonday = (year: number, month: number): Moment => {
  const d = moment({ year, month, day: 1 }).endOf('month').startOf('day');
  return d.subtract((d.day() + 6) % 7, 'day');
};

const substitute = (d: Moment, taken: Set<string>): Moment => {
  const s = d.clone();
  while (isWeekend(s) || taken.has(iso(s))) s.add(1, 'day');
  return s;
};

const holidayCache = new Map<number, Set<string>>();

const getEnglandWalesBankHolidays = (year: number): Set<string> => {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const holidays = new Set<string>();
  const easter = easterSunday(year);

  holidays.add(iso(easter.clone().subtract(2, 'day'))); // Good Friday
  holidays.add(iso(easter.clone().add(1, 'day'))); // Easter Monday
  holidays.add(iso(firstMonday(year, 4))); // Early May (first Mon of May)
  holidays.add(iso(lastMonday(year, 4))); // Spring (last Mon of May)
  holidays.add(iso(lastMonday(year, 7))); // Summer (last Mon of August)

  const fixed = [
    moment({ year, month: 0, day: 1 }), // New Year's Day
    moment({ year, month: 11, day: 25 }), // Christmas Day
    moment({ year, month: 11, day: 26 }), // Boxing Day
  ];
  fixed.filter((d) => !isWeekend(d)).forEach((d) => holidays.add(iso(d)));
  fixed
    .filter((d) => isWeekend(d))
    .forEach((d) => holidays.add(iso(substitute(d, holidays))));

  ONE_OFF_HOLIDAYS.filter((s) => s.startsWith(String(year))).forEach((s) =>
    holidays.add(s),
  );

  holidayCache.set(year, holidays);
  return holidays;
};

export const isWorkingDay = (date: Moment): boolean =>
  !isWeekend(date) && !getEnglandWalesBankHolidays(date.year()).has(iso(date));

/** N working days after `date` (skips weekends + E&W bank holidays). */
export const addWorkingDays = (date: Moment, n: number): Moment => {
  const d = date.clone();
  let remaining = n;
  while (remaining > 0) {
    d.add(1, 'day');
    if (isWorkingDay(d)) remaining -= 1;
  }
  return d;
};
